import { ApiResponse, BASE_URL, getFromServer, postToServer } from "server";
import {
  AddFilesInput,
  AddMessageInput,
  CreatePrivateConversationInput,
  CreateProblemConversationInput,
  CreateSupportConversationFromUserInput,
  CreateSupportConversationInput,
  FetchConversationsInput,
  FetchMessagesInput,
  MarkMessagesReadInput,
  UnreadConversationsCountInput,
  UnreadMessagesCountInput,
} from "../InputsTypes";
import {
  AddFilesOutput,
  AddMessageOutput,
  CreatePrivateConversationOutput,
  CreateProblemConversationOutput,
  CreateSupportConversationFromUserOutput,
  CreateSupportConversationOutput,
  FetchConversationsOutput,
  FetchMessagesOutput,
  MarkMessagesReadOutput,
  UnreadConversationsCountOutput,
  UnreadMessagesCountOutput,
} from "../OutputsTypes";

// #/conversations/support/create/from-admin

export async function createSupportConversation(
  input: CreateSupportConversationInput,
): Promise<ApiResponse<CreateSupportConversationOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/support/create/from-admin`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(input),
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: CreateSupportConversationOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/support/create/from-user

export async function createSupportConversationFromUser(
  input: CreateSupportConversationFromUserInput,
): Promise<ApiResponse<CreateSupportConversationFromUserOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/support/create/from-user`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(input),
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: CreateSupportConversationFromUserOutput =
        await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/private/create

export async function createPrivateConversation(
  input: CreatePrivateConversationInput,
): Promise<ApiResponse<CreatePrivateConversationOutput>> {
  try {
    const response = await fetch(`${BASE_URL}/conversations/private/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(input),
      credentials: "include",
    });

    if (response.ok) {
      const data: CreatePrivateConversationOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/:conversationId/messages/add

export async function addMessage(
  conversationId: string,
  input: AddMessageInput,
): Promise<ApiResponse<AddMessageOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/messages/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(input),
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: AddMessageOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/:conversationId/files/add

export async function addFiles(
  conversationId: string,
  input: AddFilesInput,
): Promise<ApiResponse<AddFilesOutput>> {
  try {
    const formData = new FormData();
    input.files.forEach((file) => formData.append("files", file, file.name));

    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/files/add`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
        },
        body: formData,
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: AddFilesOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/mine

export async function getConversations(
  input: FetchConversationsInput,
): Promise<ApiResponse<FetchConversationsOutput>> {
  try {
    const queryParams = new URLSearchParams({
      conversationType: input.conversationType,
    }).toString();

    const response = await fetch(
      `${BASE_URL}/conversations/mine?${queryParams}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: FetchConversationsOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/{conversationId}/messages

export async function getMessages(
  input: FetchMessagesInput,
): Promise<ApiResponse<FetchMessagesOutput>> {
  return await getFromServer<FetchMessagesOutput>(
    `/conversations/${input.conversationId}/messages`,
  );
}

// #/conversations/{conversationId}/messages/read

export async function markMessagesRead(
  input: MarkMessagesReadInput,
): Promise<ApiResponse<MarkMessagesReadOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/${input.conversationId}/messages/read`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
        },
        body: "",
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: MarkMessagesReadOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/unread/count

export async function getUnreadConversationsCount(
  input: UnreadConversationsCountInput,
): Promise<ApiResponse<UnreadConversationsCountOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/unread/count?conversationType=${input.conversationType}`,
      {
        method: "GET",
        headers: {
          accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: UnreadConversationsCountOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/messages/unread/count

export async function getUnreadMessagesCount(
  input: UnreadMessagesCountInput,
): Promise<ApiResponse<UnreadMessagesCountOutput>> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/messages/unread/count?conversationType=${input.conversationType}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
        },
        body: "",
        credentials: "include",
      },
    );

    if (response.ok) {
      const data: UnreadMessagesCountOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

// #/conversations/problem/create

export async function createProblemConversation(
  input: CreateProblemConversationInput,
): Promise<ApiResponse<CreateProblemConversationOutput>> {
  try {
    const formData = new FormData();
    formData.append("body", input.body);
    formData.append("exerciseId", input.exerciseId);
    input.files.forEach((file) => {
      formData.append("files", file, file.name);
    });

    const response = await fetch(`${BASE_URL}/conversations/problem/create`, {
      method: "POST",
      headers: {
        accept: "application/json",
      },
      body: formData,
      credentials: "include",
    });

    if (response.ok) {
      const data: CreateProblemConversationOutput = await response.json();
      return {
        status: 200,
        body: data,
      };
    } else {
      const errorData = await response.json();
      return {
        status: response.status,
        body: { message: errorData.message || "An error occurred" },
      };
    }
  } catch (error) {
    return {
      status: 500,
      body: { message: "Internal Server Error" },
    };
  }
}

export interface Conversation {
  _id: string;
  title: string;
  lastMessage: {
    content: string;
    createdAt: string;
    sender: {
      firstname: string;
      lastname: string;
    };
  };
  participants: {
    _id: string;
    firstname: string;
    lastname: string;
    role: string;
  }[];
  createdAt: string;
  updatedAt: string;
  status: string;
  subject: string;
  unreadCount: number;
}

// Route : GET /conversations/mine
export const getMyConversations = async (
  limit: number = 3,
): Promise<ApiResponse<Conversation[]>> => {
  return await getFromServer<Conversation[]>(
    `/conversations/mine?limit=${limit}`,
  );
};

// Route : GET /conversations/:id
export const getConversationById = async (
  conversationId: string,
): Promise<ApiResponse<Conversation>> => {
  return await getFromServer<Conversation>(`/conversations/${conversationId}`);
};
